import { TextBadge } from '@carvana/ds-badge';
import Button, { NavigableButton } from '@carvana/ds-button';
import LinkButton from '@carvana/ds-link-button';
import { decorative, icon } from '@carvana/ds-styles';
import { InventoryTypeKey } from '@carvana/merch-contracts';
import cx from 'classnames';
import { Check, X } from 'lucide-react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import Skeleton from '@/common/helios/Skeleton';
import Image from '@/common/Image';
import TransitInfo from '@/common/TransitInfo';
import DeltasContent from '@/new-cars/Modals/ViewVehiclesModal/MatchingVehicleCard/DeltasContent';
import TransitChargeState from '~/constants/common/TransitChargeState';
import { assetsCdnUrl } from '~/constants/environment';
import { SectionNameToLevelMap } from '~/constants/new-cars/SectionName';
import { useMatchingVehiclesContext } from '~/context/new-cars/MatchingVehiclesContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import useClosestVehicle from '~/hooks/new-cars/selectors/useClosestVehicle';
import type { MatchingVehicleWithDeltas } from '~/hooks/new-cars/selectors/useMatchingVehiclesWithDeltas';
import useYourSelections from '~/hooks/new-cars/selectors/useYourSelections';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import optimizeImage from '~/utilities/optimizeImage';
import {
  Card,
  ConfettiOverlay,
  Deltas,
  DeltasCard,
  Fulfillment,
  FulfillmentDot,
  GetItBy,
  IncludesAllRow,
  PreOrderBadgeWrapper,
  Price,
  PriceSection,
  SelectionsLabel,
  TrimHeading,
  Vin
} from './MatchingVehicleCard.styles';

type OwnProps = {
  vehicle: MatchingVehicleWithDeltas;
};

export default function MatchingVehicleCard({ vehicle }: OwnProps) {
  const { activeSection, selectedTrim } = useContext(ParentModelContext);
  const cardRef = useRef<HTMLElement>(null);
  const deltasCardRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    baseModel,
    deltas,
    groupCount,
    imageUrl,
    incentivizedPrice,
    trim,
    vehicleId,
    vehicleInventoryType,
    vin
  } = vehicle;
  const { fulfillment, pricing } = useMatchingVehiclesContext();
  const closestVehicle = useClosestVehicle();
  const yourSelections = useYourSelections();

  const isExactMatch = closestVehicle?.vehicleId === vehicleId;
  const isPreOrder = vehicleInventoryType === InventoryTypeKey.WIP;
  const siblingVehicleCount = groupCount ? groupCount - 1 : 0;

  const vehicleName = [baseModel, trim].filter(Boolean).join(' ');
  const getItBy = fulfillment.data[vehicleId];
  const transitCharge = pricing.data[vehicleId];

  const showShipping =
    pricing.isLoading ||
    (transitCharge && transitCharge.state !== TransitChargeState.Unknown);
  const showGetItBy = fulfillment.isLoading || Boolean(getItBy);

  const deltasToShow = useMemo(() => deltas.slice(0, 3), [deltas]);

  const viewMore = useCallback(() => {
    if (!cardRef.current || !deltasCardRef.current?.parentElement) return;

    const cardRect = cardRef.current.getBoundingClientRect();
    const deltasContentParentRect =
      deltasCardRef.current.parentElement.getBoundingClientRect();
    const deltasContentHeight = deltasContentParentRect.top - cardRect.top - 24;

    deltasCardRef.current.style.height = `${deltasContentHeight}px`;
    setIsExpanded(true);
  }, []);

  return (
    <Card data-testid={`matching-vehicle-card-${vehicleId}`} ref={cardRef}>
      {isPreOrder && (
        <PreOrderBadgeWrapper data-testid="pre-order-badge">
          <TextBadge
            className="bg-background-default"
            customBorderColor={decorative.preOrder}
            priority="custom"
            variant="outline"
          >
            Pre-order now
          </TextBadge>
        </PreOrderBadgeWrapper>
      )}

      <Image
        alt={vehicleName}
        className="w-full object-cover"
        wrapperClassName={cx('w-full relative aspect-[200/107]', {
          'before:content-[""] before:absolute before:inset-0 before:bg-canvas-overlay/70':
            isExpanded
        })}
        loading="lazy"
        src={optimizeImage(imageUrl, { width: 400 })}
      />

      {!selectedTrim && <TrimHeading>{trim}</TrimHeading>}

      <Deltas>
        {isExactMatch ? (
          <IncludesAllRow>
            <Check color={icon.success} size={16} />
            <SelectionsLabel>
              Includes all {yourSelections.length} of your selections
            </SelectionsLabel>
          </IncludesAllRow>
        ) : (
          deltasToShow.length > 0 && (
            <>
              <DeltasContent deltas={deltasToShow} />

              {deltasToShow.length < deltas.length && (
                <LinkButton
                  size="sm"
                  underline={false}
                  className="w-fit"
                  onClick={viewMore}
                  data-analytics-attributes={JSON.stringify({
                    level: SectionNameToLevelMap[activeSection],
                    section: 'Matching Vehicles Modal',
                    text: `+${deltas.length - deltasToShow.length} more`
                  })}
                  data-analytics-id="New Cars"
                  data-testid="expand-deltas-button"
                >
                  +{deltas.length - deltasToShow.length} more
                </LinkButton>
              )}
            </>
          )
        )}
      </Deltas>

      <PriceSection>
        <Price>${formatAndDropDecimal(incentivizedPrice)}</Price>
        <Fulfillment>
          {pricing.isLoading ? (
            <Skeleton
              className="w-20"
              data-testid="shipping-skeleton"
              height={20}
            />
          ) : transitCharge &&
            transitCharge.state !== TransitChargeState.Unknown ? (
            <TransitInfo transitCharge={transitCharge} />
          ) : null}
          {showShipping && showGetItBy && <FulfillmentDot aria-hidden="true" />}
          {fulfillment.isLoading ? (
            <Skeleton
              className="w-20"
              data-testid="get-it-by-skeleton"
              height={20}
            />
          ) : getItBy ? (
            <GetItBy>Get it {getItBy}</GetItBy>
          ) : null}
        </Fulfillment>
      </PriceSection>

      <div className="relative">
        <DeltasCard
          ref={deltasCardRef}
          className={isExpanded ? undefined : 'hidden'}
        >
          {isExpanded && (
            <>
              <Button
                variant="tertiary"
                size="xs"
                className="absolute right-md top-md"
                icon
                onClick={() => {
                  deltasCardRef.current?.style.removeProperty('height');
                  setIsExpanded(false);
                }}
                data-analytics-attributes={JSON.stringify({
                  level: SectionNameToLevelMap[activeSection],
                  section: 'Matching Vehicles Modal',
                  text: 'Close deltas'
                })}
                data-analytics-id="New Cars"
                data-testid="matching-vehicle-close-deltas"
              >
                <X size={18} />
              </Button>

              <Deltas>
                <DeltasContent deltas={deltas} />
              </Deltas>
            </>
          )}
        </DeltasCard>

        <div className="px-xl">
          <NavigableButton
            variant={isExactMatch ? 'primary' : 'secondary'}
            size="sm"
            className="w-full"
            href={`/vehicle/${vehicleId}`}
            data-analytics-attributes={JSON.stringify({
              level: SectionNameToLevelMap[activeSection],
              section: 'Matching Vehicles Modal',
              text: 'View Vehicle',
              vehicleId
            })}
            data-analytics-id="New Cars"
            data-testid="matching-vehicle-view-vehicle"
          >
            View Vehicle
          </NavigableButton>
        </div>
      </div>

      <Vin>
        VIN {vin} {siblingVehicleCount ? `+${siblingVehicleCount} more` : null}
      </Vin>

      {isExactMatch && (
        <ConfettiOverlay
          alt="confetti"
          src={`${assetsCdnUrl}/merchui/gifs/confetti.gif`}
        />
      )}
    </Card>
  );
}
